'use client'
import React, { useContext, useRef } from 'react'
import classNames from 'classnames'
import useCarousel from 'hooks/useCarousel'
import { DotIndicators } from 'components/DotIndicators'
import { ChevronLeft, ChevronRight } from 'react-feather'
import { IconButton } from '@achieve/ascend'
import { LayoutContext } from 'providers/LayoutProvider'
import { SITE_ENVIRONMENTS } from 'constants/websites'

export function BaseCarousel({
  items = [],
  renderItem,
  autoPlay = false,
  interval = 4000,
  loop = false,
  threshold = 0.5,
  hideControls = false,
  hideIndicators = false,
  enableMouseDrag = false,
  classNameContainer,
  classNameTrack,
  classNameSlide,
  classNameControls,
  classNameControlsLeft,
  classNameControlsRight,
  classNameIndicators,
  indicatorVariant,
  dataTestId = 'base-carousel',
}) {
  const {
    containerRef,
    slideRefs,
    activeIndex,
    scrollToSlide,
    scrollToNext,
    scrollToPrev,
    setIsPaused,
    isPrevDisabled,
    isNextDisabled,
  } = useCarousel({ itemsLength: items.length, autoPlay, interval, loop, threshold })

  const { state: { siteEnv } = {} } = useContext(LayoutContext)

  const isDragging = useRef(false)
  const startX = useRef(0)
  const scrollLeft = useRef(0)

  if (!items.length || typeof renderItem !== 'function') return null

  return (
    <div
      role="region"
      aria-roledescription="carousel"
      aria-label="Carousel"
      className={classNames('relative group flex flex-col items-center w-full', classNameContainer)}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => {
        setIsPaused(false)
        if (enableMouseDrag) isDragging.current = false
      }}
      onTouchStart={() => setIsPaused(true)}
      onTouchEnd={() => setIsPaused(false)}
      data-testid={dataTestId}
    >
      {/* Controls: Left */}
      {!hideControls && (
        <div
          className={classNames(
            'absolute left-0 z-10 top-1/2 -translate-y-1/2 hidden lg:block',
            classNameControls,
            classNameControlsLeft
          )}
        >
          <IconButton
            onClick={scrollToPrev}
            disabled={isPrevDisabled}
            aria-label="Display previous item in carousel"
            className={classNames(
              'bg-white shadow-md rounded-full h-[50px] w-[50px] hover:bg-blue-100',
              siteEnv === SITE_ENVIRONMENTS.FDR ? '[&_svg]:text-[#154199]' : '[&_svg]:text-[#30f]'
            )}
            data-testid={`${dataTestId}-arrow-left`}
          >
            <ChevronLeft />
          </IconButton>
        </div>
      )}

      {/* Track */}
      <div
        ref={containerRef}
        className={classNames(
          'w-full flex overflow-x-scroll snap-x snap-mandatory scrollbar-hide',
          enableMouseDrag ? 'cursor-grab active:cursor-grabbing' : 'scroll-smooth',
          classNameTrack
        )}
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        onMouseDown={
          enableMouseDrag
            ? (e) => {
                isDragging.current = true
                startX.current = e.pageX - containerRef.current.offsetLeft
                scrollLeft.current = containerRef.current.scrollLeft
              }
            : undefined
        }
        onMouseUp={
          enableMouseDrag
            ? () => {
                isDragging.current = false
              }
            : undefined
        }
        onMouseLeave={
          enableMouseDrag
            ? () => {
                isDragging.current = false
              }
            : undefined
        }
        onMouseMove={
          enableMouseDrag
            ? (e) => {
                if (!isDragging.current) return
                e.preventDefault()
                const x = e.pageX - containerRef.current.offsetLeft
                const walk = (x - startX.current) * 1.5
                containerRef.current.scrollLeft = scrollLeft.current - walk
              }
            : undefined
        }
        data-testid="carousel-items-container"
      >
        {items.map((item, index) => (
          <div
            key={item?.sys?.id || item?.id || index}
            data-index={index}
            ref={(el) => (slideRefs.current[index] = el)}
            role="group"
            aria-roledescription="slide"
            aria-label={`Slide ${index + 1} of ${items.length}`}
            className={classNames('snap-center shrink-0', classNameSlide)}
            data-testid={`${dataTestId}-slide-${index}`}
          >
            {renderItem(item, index, activeIndex === index)}
          </div>
        ))}
      </div>

      {/* Controls: Right */}
      {!hideControls && (
        <div
          className={classNames(
            'absolute right-0 z-10 top-1/2 -translate-y-1/2 hidden lg:block',
            classNameControls,
            classNameControlsRight
          )}
        >
          <IconButton
            onClick={scrollToNext}
            disabled={isNextDisabled}
            aria-label="Display next item in carousel"
            className={classNames(
              'bg-white shadow-md rounded-full h-[50px] w-[50px] hover:bg-blue-100',
              siteEnv === SITE_ENVIRONMENTS.FDR ? '[&_svg]:text-[#154199]' : '[&_svg]:text-[#30f]'
            )}
            data-testid={`${dataTestId}-arrow-right`}
          >
            <ChevronRight />
          </IconButton>
        </div>
      )}

      {/* Indicators */}
      {!hideIndicators && (
        <DotIndicators
          size={items.length}
          activeIndex={activeIndex}
          variant={indicatorVariant}
          OnClickDot={scrollToSlide}
          classNameIndicators={classNames('mt-8', classNameIndicators)}
        />
      )}
    </div>
  )
}
